// assets/countdown.js
import { Component } from '@theme/component';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * @typedef {object} CountdownRefs
 * @property {HTMLElement} root - The element carrying the target date.
 * @property {HTMLElement} [days] - The days display.
 * @property {HTMLElement} [hours] - The hours display.
 * @property {HTMLElement} [minutes] - The minutes display.
 * @property {HTMLElement} [seconds] - The seconds display.
 */

/**
 * A live countdown timer that reloads the page once the target date is reached.
 * @extends Component<CountdownRefs>
 */
class CountdownTimer extends Component {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intervalId !== undefined) clearInterval(this.#intervalId);
  }

  #start() {
    const { root } = this.refs;
    if (!root) return;

    const targetValue = root.dataset.target;
    if (!targetValue) return;

    const target = new Date(targetValue).getTime();
    if (Number.isNaN(target)) return;

    this.#tick(target);
    this.#intervalId = window.setInterval(() => this.#tick(target), SECOND);
  }

  /** @param {number} target */
  #tick(target) {
    const remaining = target - Date.now();

    if (remaining <= 0) {
      if (this.#intervalId !== undefined) clearInterval(this.#intervalId);
      window.location.reload();
      return;
    }

    const { days, hours, minutes, seconds } = this.refs;

    if (days) days.textContent = String(Math.floor(remaining / DAY)).padStart(2, '0');
    if (hours) hours.textContent = String(Math.floor((remaining % DAY) / HOUR)).padStart(2, '0');
    if (minutes) minutes.textContent = String(Math.floor((remaining % HOUR) / MINUTE)).padStart(2, '0');
    if (seconds) seconds.textContent = String(Math.floor((remaining % MINUTE) / SECOND)).padStart(2, '0');
  }
}

customElements.define('countdown-timer-component', CountdownTimer);
